/**
 * A room's cleaning status for the Overall View (Cleaning Visual, z8nrz7f731),
 * derived from its facts against the current time — and everything the view
 * counts from it, so the summary bar, the KPI cards, the floor strips, the room
 * cards and the detail panel can never disagree.
 *
 * The six statuses are the spec's legend:
 *   Released          cleaned, verified and inside its hold time; safe to use
 *   Expiring          the hold time ends within 4 hours; schedule now
 *   Expired           the hold time lapsed; not available for processing
 *   Cleaning          a clean is in progress against its SOP
 *   Awaiting review   cleaned, pending QA verification
 *   Out of service    maintenance, deviation or quarantine; blocked
 */
import type { OverviewRoom } from '@/api/facilityApi'

export type RoomStatus = 'released' | 'expiring' | 'expired' | 'cleaning' | 'awaiting' | 'out_of_service'

/** The hold time's last stretch, shown as Expiring. */
export const EXPIRING_WITHIN_MS = 4 * 60 * 60 * 1000

export const STATUS_ORDER: readonly RoomStatus[] = ['released', 'expiring', 'expired', 'cleaning', 'awaiting', 'out_of_service']

/** Most urgent first: what blocks work, then what is about to. */
const URGENCY: Record<RoomStatus, number> = {
  expired: 0,
  expiring: 1,
  out_of_service: 2,
  awaiting: 3,
  cleaning: 4,
  released: 5,
}

export interface StatusMeta {
  label: string
  /** The KPI card's caption. */
  caption: string
  /** The legend's line. */
  description: string
  /** Strong colour: borders, text, icons. */
  color: string
  /** Pale fill: pills. */
  tint: string
}

export const STATUS_META: Record<RoomStatus, StatusMeta> = {
  released: {
    label: 'Released',
    caption: 'Released',
    description: 'Cleaned, verified and inside hold time. Safe to use.',
    color: '#1f8a3b',
    tint: '#e9f6ed',
  },
  expiring: {
    label: 'Expiring',
    caption: 'Expiring',
    description: 'Clean hold time ends within 4 hours. Schedule now.',
    color: '#d9770a',
    tint: '#fef3e2',
  },
  expired: {
    label: 'Expired',
    caption: 'Expired',
    description: 'Hold time lapsed. Room is NOT available for processing.',
    color: '#d32626',
    tint: '#fdeaea',
  },
  cleaning: {
    label: 'Cleaning',
    caption: 'Cleaning',
    description: 'Clean in progress against the logged SOP.',
    color: '#2459d6',
    tint: '#e8effd',
  },
  awaiting: {
    label: 'Awaiting review',
    caption: 'Awaiting review',
    description: 'Cleaned, pending QA verification or EM result.',
    color: '#0f8f9c',
    tint: '#e4f6f7',
  },
  out_of_service: {
    label: 'Out of service',
    caption: 'Out of service',
    description: 'Maintenance, deviation or quarantine. Blocked.',
    color: '#7c3aed',
    tint: '#f2ecfd',
  },
}

/** When the clean expires, if it does: the last clean's end plus the hold time. */
export function cleanExpiresAt(room: Pick<OverviewRoom, 'lastCleanEndedAt' | 'holdTimeHours'>): Date | null {
  if (!room.lastCleanEndedAt || !room.holdTimeHours) return null
  return new Date(new Date(room.lastCleanEndedAt).getTime() + room.holdTimeHours * 3_600_000)
}

export function roomStatus(room: OverviewRoom, now: Date): RoomStatus {
  if (room.outOfService) return 'out_of_service'
  if (room.active) return 'cleaning'
  if (room.awaitingRelease) return 'awaiting'
  // Never cleaned: there is no clean to hold, so the room is not available.
  if (!room.lastCleanEndedAt) return 'expired'
  const expires = cleanExpiresAt(room)
  if (!expires) return 'released'
  const left = expires.getTime() - now.getTime()
  if (left <= 0) return 'expired'
  return left <= EXPIRING_WITHIN_MS ? 'expiring' : 'released'
}

/** "9h 40m", "35m", "2d 3h". */
export function formatSpan(ms: number): string {
  const mins = Math.max(0, Math.round(ms / 60_000))
  const d = Math.floor(mins / 1440)
  const h = Math.floor((mins % 1440) / 60)
  const m = mins % 60
  if (d > 0) return `${d}d ${h}h`
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}

const hhmm = (iso: string) => {
  const d = new Date(iso)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

/** The time line under a room card's pill. */
export function roomFooter(room: OverviewRoom, now: Date): string {
  const status = roomStatus(room, now)
  const expires = cleanExpiresAt(room)
  switch (status) {
    case 'out_of_service':
      return room.statusNote?.trim() || 'Out of service'
    case 'cleaning':
      return room.active?.startedAt ? `Started ${hhmm(room.active.startedAt)}` : 'Cleaning started'
    case 'awaiting':
      return 'QA sign-off pending'
    case 'expired':
      if (!room.lastCleanEndedAt) return 'No clean recorded yet'
      return expires ? `Expired ${formatSpan(now.getTime() - expires.getTime())} ago` : 'Expired'
    case 'expiring':
    case 'released':
      return expires ? `Expires in ${formatSpan(expires.getTime() - now.getTime())}` : 'No hold time set'
  }
}

/** Progress of the clean in progress, 0–100, or null with no steps to count. */
export function cleaningProgress(room: OverviewRoom): number | null {
  const a = room.active
  if (!a || a.stepsTotal === 0) return null
  return Math.round((a.stepsDone / a.stepsTotal) * 100)
}

export type RoomSort = 'urgency' | 'grade' | 'room'

const byLabel = (a: OverviewRoom, b: OverviewRoom) => a.label.localeCompare(b.label, undefined, { numeric: true })

export function sortRooms(rooms: readonly OverviewRoom[], sort: RoomSort, now: Date): OverviewRoom[] {
  const list = [...rooms]
  if (sort === 'room') return list.sort(byLabel)
  if (sort === 'grade') {
    // Grade A first; rooms with no grade last.
    return list.sort((a, b) => (a.grade ?? '￿').localeCompare(b.grade ?? '￿') || byLabel(a, b))
  }
  return list.sort((a, b) => {
    const d = URGENCY[roomStatus(a, now)] - URGENCY[roomStatus(b, now)]
    if (d !== 0) return d
    // Within a status, whichever runs out first.
    const ea = cleanExpiresAt(a)?.getTime() ?? Infinity
    const eb = cleanExpiresAt(b)?.getTime() ?? Infinity
    return ea - eb || byLabel(a, b)
  })
}

export type StatusCounts = Record<RoomStatus, number>

export function countStatuses(rooms: readonly OverviewRoom[], now: Date): StatusCounts {
  const counts = Object.fromEntries(STATUS_ORDER.map((s) => [s, 0])) as StatusCounts
  for (const r of rooms) counts[roomStatus(r, now)]++
  return counts
}

/** The summary bar across the whole campus. */
/** The campus summary chips, in order. A room can count under more than one (clean and overdue). */
export type SummaryKey = 'overdue' | 'dueSoon' | 'cleaning' | 'awaiting' | 'clean' | 'outOfService'
export const SUMMARY_KEYS: readonly SummaryKey[] = ['overdue', 'dueSoon', 'cleaning', 'awaiting', 'clean', 'outOfService']

/** Whether a room counts under a summary chip: the one rule for its number and for the rooms it lists. */
export function inSummary(key: SummaryKey, room: OverviewRoom, now: Date): boolean {
  switch (key) {
    case 'overdue':
      return room.overdue
    case 'dueSoon':
      return room.dueSoon
    case 'cleaning':
      return roomStatus(room, now) === 'cleaning'
    case 'awaiting':
      return roomStatus(room, now) === 'awaiting'
    case 'clean': {
      const s = roomStatus(room, now)
      return s === 'released' || s === 'expiring'
    }
    case 'outOfService':
      return roomStatus(room, now) === 'out_of_service'
  }
}

export function summary(rooms: readonly OverviewRoom[], now: Date): Record<SummaryKey, number> {
  const out = { overdue: 0, dueSoon: 0, cleaning: 0, awaiting: 0, clean: 0, outOfService: 0 }
  for (const k of SUMMARY_KEYS) out[k] = rooms.filter((r) => inSummary(k, r, now)).length
  return out
}

/** Rooms in a building that need someone: expired, expiring or overdue. The red badge on the campus map. */
export function attentionCount(rooms: readonly OverviewRoom[], now: Date): number {
  return rooms.filter((r) => {
    const s = roomStatus(r, now)
    return s === 'expired' || s === 'expiring' || r.overdue
  }).length
}

/** Matches the search box: room, name, grade, type or operator. */
export function matchesSearch(room: OverviewRoom, q: string): boolean {
  const term = q.trim().toLowerCase()
  if (!term) return true
  return [room.label, room.name, room.grade, room.typeName, room.operator, room.sop]
    .filter(Boolean)
    .some((v) => (v as string).toLowerCase().includes(term))
}

// ── Layout #2: one colour per room ──────────────────────────────────────────

/**
 * Layout #2's status bars (Cleaning Visual p.5) split a building's rooms into
 * coloured segments, so each room must land in exactly ONE bucket — unlike
 * `summary`, whose counts overlap (a clean room can also be overdue). Checked
 * most pressing first: out of service, being cleaned, waiting on QA, then
 * whether it needs a clean now (expired or overdue) or soon (expiring or due
 * soon), and otherwise clean.
 */
export type BarBucket = 'attention' | 'soon' | 'progress' | 'qa' | 'clean' | 'out'

/** Bar order, left to right, as in the spec's bars. */
export const BAR_ORDER: readonly BarBucket[] = ['attention', 'soon', 'progress', 'qa', 'clean', 'out']

export const BAR_META: Record<BarBucket, { color: string; label: string }> = {
  attention: { color: '#d32626', label: 'expired or overdue' },
  soon: { color: '#d9970a', label: 'due soon' },
  progress: { color: '#2459d6', label: 'in progress' },
  qa: { color: '#b0328d', label: 'awaiting QA' },
  clean: { color: '#1f8a3b', label: 'clean' },
  out: { color: '#4b5563', label: 'out of service' },
}

export function barBucket(room: OverviewRoom, now: Date): BarBucket {
  const s = roomStatus(room, now)
  if (s === 'out_of_service') return 'out'
  if (s === 'cleaning') return 'progress'
  if (s === 'awaiting') return 'qa'
  if (s === 'expired' || room.overdue) return 'attention'
  if (s === 'expiring' || room.dueSoon) return 'soon'
  return 'clean'
}

export function barCounts(rooms: readonly OverviewRoom[], now: Date): Record<BarBucket, number> {
  const out: Record<BarBucket, number> = { attention: 0, soon: 0, progress: 0, qa: 0, clean: 0, out: 0 }
  for (const r of rooms) out[barBucket(r, now)]++
  return out
}

// ── Next scheduled clean ─────────────────────────────────────────────────────

export interface NextClean {
  /** `YYYY-MM-DD`. */
  date: string
  label: string | null
  /** True when it comes from the room type's cycle and no task exists for it yet. */
  fromCycle: boolean
}

/**
 * The room's next clean: its next open task, or else — sooner if it is — the
 * next date its room type's cycle falls on. A cycle is only a schedule until
 * its tasks are generated (Calendar → Occurrences → Generate Tasks), so a room
 * can be scheduled with no task yet; saying "—" there read as unscheduled.
 */
export function nextScheduled(room: OverviewRoom): NextClean | null {
  const cycle = room.nextCycle
  const task = room.nextDueDate
  if (task && (!cycle || task <= cycle.date)) return { date: task, label: null, fromCycle: false }
  return cycle ? { date: cycle.date, label: cycle.label, fromCycle: true } : null
}
